/**
 * Tokens de AntD y la config de `cssVar`.
 *
 * Extraído de `theme-silia.ts` (paso 03, ver `theme/spec.md` §9).
 */

import type { ThemeConfig } from 'antd';
import {
  brand,
  surface,
  ink,
  line,
  semantic,
  fill,
  elevation,
  typography,
  radius,
  space,
  control,
} from './primitives';

/* ═══════════════════════════════════════════════════════════════════════
   7. ANT DESIGN CONFIG
   ═══════════════════════════════════════════════════════════════════════ */

/**
 * AntD `ConfigProvider` tokens. Literal values, not `var()` — AntD parses these
 * to derive colour ramps (hover, active, disabled) at build time, and a `var()`
 * string breaks that derivation silently: you get a component with no hover
 * state and no error.
 *
 * Which means: switching template at runtime repaints hand-written CSS via the
 * body class, but AntD primitives need `themeFor(id)` handed to ConfigProvider.
 * Both mechanisms, one source. See `TEMPLATES` below.
 */
export const antdToken: ThemeConfig['token'] = {
  /* Shape & rhythm */
  borderRadius: radius.md,
  borderRadiusLG: radius.md,
  borderRadiusSM: radius.sm,
  borderRadiusXS: radius.xs,
  controlHeight: control.height,
  fontFamily: typography.fontSans,
  fontSize: typography.fontSize,
  lineHeight: typography.lineHeight,
  /* Headings. Without these AntD derives its own ramp and `level={1}` lands on
     38px, which is why three stylesheets forced it back to 30. */
  fontSizeHeading1: typography.fontSizeHeading1,
  fontSizeHeading2: typography.fontSizeHeading2,
  fontSizeHeading3: typography.fontSizeHeading3,
  fontSizeHeading4: typography.fontSizeHeading4,
  fontSizeHeading5: typography.fontSizeHeading5,
  lineHeightHeading1: typography.lineHeightHeading1,
  lineHeightHeading2: typography.lineHeightHeading2,
  lineHeightHeading3: typography.lineHeightHeading3,
  lineHeightHeading4: typography.lineHeightHeading4,
  lineHeightHeading5: typography.lineHeightHeading5,
  padding: space.md,
  paddingLG: space.lg,
  paddingSM: space.sm,
  paddingXS: space.xs,
  paddingXXS: space.xxs,
  margin: space.md,
  marginLG: space.lg,
  marginSM: space.sm,
  marginXS: space.xs,
  marginXXS: space.xxs,

  /* Colour */
  colorPrimary: brand.lime,
  colorBgLayout: surface.bgLayout,
  colorBgContainer: surface.bgContainer,
  colorBgElevated: surface.bgElevated,
  colorBgContainerDisabled: surface.tint3,
  colorText: ink.text,
  colorTextDescription: ink.textDescription,
  colorTextSecondary: ink.textDescription,
  colorTextTertiary: ink.textIcon,
  colorTextQuaternary: ink.textPlaceholder,
  colorTextPlaceholder: ink.textPlaceholder,
  colorBorder: line.border,
  colorBorderSecondary: line.borderSubtle,
  colorSplit: line.split,
  colorFillContent: line.fillContent,
  colorBgTextHover: fill.f2,
  colorFillSecondary: fill.f3,
  colorFillTertiary: fill.f2,
  colorFillQuaternary: fill.f1,
  colorSuccess: semantic.successFg,
  colorWarning: semantic.warningFg,
  colorError: semantic.dangerFg,
  colorErrorBg: semantic.dangerBg,
  colorInfo: semantic.infoFg,
  // Declared like `colorErrorBg` above, and for the same reason: given only
  // `colorInfo`, Ant Design DERIVES the panel background with its own algorithm
  // and that derived value wins over `components.Alert.colorInfoBg`. With the
  // brand ramp as the seed the derivation returns a desaturated grey (#c4c0cc),
  // not the intended n100. Pinning it here is what actually paints the panel.
  colorInfoBg: semantic.infoBg,
  colorInfoBorder: semantic.infoBd,

  /* Elevation */
  boxShadow: elevation.sm,
  boxShadowSecondary: elevation.md,
};
